<template>
  <div
    v-if="consentState.settingsOpen"
    class="cookie-settings-backdrop"
    @click.self="close"
  >
    <div
      class="cookie-settings"
      role="dialog"
      aria-modal="true"
      :aria-label="$t('cookies.modal.title')"
    >
      <header class="cookie-settings__header">
        <h2>{{ $t("cookies.modal.title") }}</h2>
        <button
          type="button"
          class="cookie-settings__close"
          :aria-label="$t('cookies.modal.close')"
          @click="close"
        >×</button>
      </header>

      <p class="cookie-settings__description">
        {{ $t("cookies.modal.description") }}
      </p>

      <div class="cookie-settings__categories">
        <div class="cookie-settings__category">
          <div class="cookie-settings__category-head">
            <h3>{{ $t("cookies.modal.categories.necessary.title") }}</h3>
            <span class="cookie-settings__always-on">
              {{ $t("cookies.modal.alwaysOn") }}
            </span>
          </div>
          <p>{{ $t("cookies.modal.categories.necessary.description") }}</p>
        </div>

        <label class="cookie-settings__category">
          <div class="cookie-settings__category-head">
            <h3>{{ $t("cookies.modal.categories.analytics.title") }}</h3>
            <input
              v-model="analytics"
              type="checkbox"
              class="cookie-settings__toggle"
            />
          </div>
          <p>{{ $t("cookies.modal.categories.analytics.description") }}</p>
        </label>

        <label class="cookie-settings__category">
          <div class="cookie-settings__category-head">
            <h3>{{ $t("cookies.modal.categories.marketing.title") }}</h3>
            <input
              v-model="marketing"
              type="checkbox"
              class="cookie-settings__toggle"
            />
          </div>
          <p>{{ $t("cookies.modal.categories.marketing.description") }}</p>
        </label>
      </div>

      <div class="cookie-settings__actions">
        <button
          type="button"
          class="cookie-settings__btn cookie-settings__btn--ghost"
          @click="rejectAll"
        >
          {{ $t("cookies.modal.rejectAll") }}
        </button>
        <button
          type="button"
          class="cookie-settings__btn cookie-settings__btn--ghost"
          @click="acceptAll"
        >
          {{ $t("cookies.modal.acceptAll") }}
        </button>
        <button
          type="button"
          class="cookie-settings__btn cookie-settings__btn--primary"
          @click="save"
        >
          {{ $t("cookies.modal.saveSelection") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  consentState,
  acceptAllCookies,
  rejectAllCookies,
  saveCookieSelection,
  closeCookieSettings,
} from "../stores/consentStore";

export default {
  name: "CookieSettings",
  data() {
    return {
      consentState,
      analytics: consentState.categories.analytics,
      marketing: consentState.categories.marketing,
    };
  },
  watch: {
    "consentState.settingsOpen"(open) {
      if (open) {
        // Sync the local toggles with the persisted state every time the
        // modal re-opens so the user always sees their current choices.
        this.analytics = consentState.categories.analytics;
        this.marketing = consentState.categories.marketing;
      }
    },
  },
  methods: {
    close() {
      closeCookieSettings();
    },
    acceptAll() {
      acceptAllCookies();
    },
    rejectAll() {
      rejectAllCookies();
    },
    save() {
      saveCookieSelection({
        analytics: this.analytics,
        marketing: this.marketing,
      });
    },
  },
};
</script>

<style scoped>
.cookie-settings-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 16px;
}

.cookie-settings {
  max-width: 560px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  background: var(--app-surface, #fff);
  color: var(--app-text, #111);
  border-radius: 14px;
  border: 1px solid var(--app-border, rgba(0, 0, 0, 0.1));
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  padding: 20px;
  font-size: 14px;
  line-height: 1.45;
}

.cookie-settings__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.cookie-settings__header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}

.cookie-settings__close {
  width: 30px;
  height: 30px;
  border-radius: 999px;
  border: 1px solid var(--app-border, rgba(0, 0, 0, 0.15));
  background: transparent;
  color: inherit;
  font-size: 18px;
  cursor: pointer;
}

.cookie-settings__description {
  margin: 0 0 16px;
  color: var(--app-text-muted, #555);
}

.cookie-settings__categories {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 18px;
}

.cookie-settings__category {
  display: block;
  border: 1px solid var(--app-border, rgba(0, 0, 0, 0.12));
  border-radius: 10px;
  padding: 12px 14px;
  cursor: pointer;
}

.cookie-settings__category h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}

.cookie-settings__category p {
  margin: 6px 0 0;
  color: var(--app-text-muted, #555);
  font-size: 13px;
}

.cookie-settings__category-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.cookie-settings__always-on {
  font-size: 12px;
  font-weight: 600;
  color: var(--app-text-muted, #888);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.cookie-settings__toggle {
  width: 18px;
  height: 18px;
  accent-color: #ffcc02;
  cursor: pointer;
}

.cookie-settings__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.cookie-settings__btn {
  padding: 9px 16px;
  border-radius: 999px;
  border: none;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
}

.cookie-settings__btn--ghost {
  background: var(--app-surface-alt, rgba(0, 0, 0, 0.05));
  color: var(--app-text, #111);
}

.cookie-settings__btn--primary {
  background: #ffcc02;
  color: #111;
}

.cookie-settings__btn--primary:hover {
  opacity: 0.92;
}

.cookie-settings__btn--ghost:hover {
  background: var(--app-surface-alt-hover, rgba(0, 0, 0, 0.08));
}
</style>
